import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, Trash2, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { daysLeftLabel, filterTrash } from "../lib/trash";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const ALL = "all";
const formatDay = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

// La corbeille de tout le CRM (Ordre 28) : ce qui a été supprimé, tous modules confondus, restaurable jusqu'à l'effacement définitif.
// Chaque personne n'y voit que les types dont elle détient le droit de suppression, et seulement ce qu'elle aurait pu ouvrir avant.
export default function Trash() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [type, setType] = useState(ALL);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const { data: body } = await api.get("/trash");
      setData(body);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => filterTrash(data?.items, { type: type === ALL ? "" : type, query }), [data, type, query]);
  const filtered = type !== ALL || query.trim() !== "";
  const reset = () => { setType(ALL); setQuery(""); };

  const restore = async (item) => {
    setBusy(`${item.resource}:${item.id}`);
    try {
      await api.post(`/trash/${item.resource}/${item.id}/restore`);
      toast.success(`${item.type_label} restauré`);
      setData((current) => ({ ...current, items: current.items.filter((i) => !(i.resource === item.resource && i.id === item.id)) }));
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de restaurer cet élément."));
    } finally {
      setBusy(null);
    }
  };

  const RestoreButton = ({ item }) => (
    <Button variant="outline" size="sm" className="shrink-0 gap-1.5" disabled={busy === `${item.resource}:${item.id}`} onClick={() => restore(item)} data-testid="trash-restore">
      <Undo2 size={14} /> Restaurer
    </Button>
  );

  return (
    <div className="space-y-4" data-testid="trash-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Corbeille</h1>
        <p className="text-sm text-slate-500">
          Ce qui a été supprimé, tous modules confondus.{data ? ` Un élément reste ici ${data.retention_days} jours : vous pouvez le restaurer, puis il est définitivement effacé.` : ""}
        </p>
      </div>

      {error && (
        <Card className="p-6 text-center" data-testid="trash-error">
          <p className="text-sm text-slate-700">Impossible de charger la corbeille.</p>
          <Button variant="outline" className="mt-3" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {!error && data === null && <div className="h-40 animate-pulse rounded-lg bg-slate-100" data-testid="trash-loading" />}

      {!error && data && (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative sm:w-80">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher dans la corbeille…" className="pl-9" aria-label="Rechercher dans la corbeille" data-testid="trash-search" />
            </div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="sm:w-60" aria-label="Type d'élément" data-testid="trash-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tous les types ({data.items.length})</SelectItem>
                {data.types.map((t) => <SelectItem key={t.resource} value={t.resource}>{t.label} ({t.count})</SelectItem>)}
              </SelectContent>
            </Select>
            {filtered && <Button variant="ghost" size="sm" className="text-slate-600" onClick={reset} data-testid="trash-reset"><X size={14} className="mr-1" />Effacer</Button>}
          </div>

          {data.items.length === 0 && (
            <Card className="p-10 text-center" data-testid="trash-empty">
              <Trash2 size={28} className="mx-auto mb-3 text-slate-400" />
              <p className="font-medium text-slate-900">La corbeille est vide</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">Ce que vous supprimez dans le CRM arrive ici et reste restaurable pendant {data.retention_days} jours.</p>
            </Card>
          )}

          {data.items.length > 0 && shown.length === 0 && (
            <Card className="p-8 text-center" data-testid="trash-no-result">
              <p className="text-sm text-slate-600">Aucun élément ne correspond.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={reset}>Effacer les filtres</Button>
            </Card>
          )}

          {shown.length > 0 && (
            <>
              <Card className="hidden overflow-hidden md:block">
                <Table data-testid="trash-list">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Élément</TableHead>
                      <TableHead>Supprimé</TableHead>
                      <TableHead>Motif</TableHead>
                      <TableHead>Effacement</TableHead>
                      <TableHead className="w-32" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shown.map((item) => (
                      <TableRow key={`${item.resource}:${item.id}`} data-testid="trash-item">
                        <TableCell className="max-w-sm">
                          <Badge variant="secondary" className="mb-1 font-normal">{item.type_label}</Badge>
                          <p className="break-words text-sm font-medium text-slate-900">{item.label}</p>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDay(item.deleted_at)}{item.deleted_by_name ? <><br />{item.deleted_by_name}</> : null}</TableCell>
                        <TableCell className="max-w-xs break-words text-sm text-slate-600">{item.delete_reason || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDay(item.purge_at)}<br /><span className="text-xs text-slate-500">{daysLeftLabel(item.purge_at)}</span></TableCell>
                        <TableCell className="text-right"><RestoreButton item={item} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>

              <ul className="space-y-2 md:hidden" data-testid="trash-cards">
                {shown.map((item) => (
                  <li key={`${item.resource}:${item.id}`} data-testid="trash-item">
                    <Card className="p-4">
                      <Badge variant="secondary" className="mb-1.5 font-normal">{item.type_label}</Badge>
                      <p className="break-words text-sm font-medium text-slate-900">{item.label}</p>
                      <p className="mt-1 text-xs text-slate-500">Supprimé le {formatDay(item.deleted_at)}{item.deleted_by_name ? ` par ${item.deleted_by_name}` : ""}</p>
                      {item.delete_reason && <p className="mt-0.5 break-words text-xs text-slate-600">Motif : {item.delete_reason}</p>}
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500">{daysLeftLabel(item.purge_at)}</span>
                        <RestoreButton item={item} />
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500" data-testid="trash-count">{shown.length} sur {data.items.length} élément{data.items.length > 1 ? "s" : ""}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
